import ordinal from "ordinal";

import { getUnitDurationFromCourseUnitTag, exists } from "@examspark/common";
import { fromAWSDate } from "@examspark/common/dist/utils/coursePlanner";
import { CourseUnit } from "@examspark/types";

import { calculateGradingPeriods } from "./calculateGradingPeriods";
import { YagDataSource, YagDataStructure } from "./types";

export const calculateYag = (
  courseUnits: CourseUnit[],
  dataSource: YagDataSource,
): YagDataStructure => {
  if (dataSource?.type === "CourseCalendar") {
    const cal = dataSource?.value;

    const numberOfGradingPeriods =
      cal.calendar?.gradingPeriodLength === "6_WEEKS" ? 6 : 4;
    const gradingPeriodNameSuffix =
      cal.calendar?.gradingPeriodLength === "6_WEEKS"
        ? "Six Weeks"
        : "Nine Weeks";

    const academicCalendar = cal.calendar;
    const units = cal.units;
    const gradingPeriods = calculateGradingPeriods(units, academicCalendar);

    const buckets: any = [];

    for (let i = 0; i < numberOfGradingPeriods; i++) {
      const units: YagDataStructure["gradingPeriods"][number]["units"] =
        gradingPeriods?.[i].units
          ?.map((unitPlan) => {
            const unit = courseUnits.find((u) => u.id === unitPlan.id)?.unit;

            if (!unit) {
              return null;
            }

            const datesInGradingPeriod = unitPlan.dates?.filter(
              (date) =>
                fromAWSDate(gradingPeriods?.[i].startAt) <= fromAWSDate(date) &&
                fromAWSDate(date) <= fromAWSDate(gradingPeriods?.[i].endAt),
            );

            return {
              unit,
              daysInUnitInGradingPeriod: datesInGradingPeriod?.length ?? 0,

              startDate: datesInGradingPeriod?.at(0),
              endDate: datesInGradingPeriod?.at(-1),

              isContinued:
                i > 0 &&
                gradingPeriods?.[i - 1].units?.find(
                  (prevBucketUnitRef) => prevBucketUnitRef.id === unitPlan.id,
                ) != null,
            };
          })
          .filter(exists) ?? [];

      const bucket: YagDataStructure["gradingPeriods"][number] = {
        name: ordinal(i + 1) + " " + gradingPeriodNameSuffix,
        semesterIndex:
          i < numberOfGradingPeriods / 2 ? (1 as const) : (2 as const),

        startAt: gradingPeriods?.[i].startAt,
        endAt: gradingPeriods?.[i].endAt,
        durationInDays: gradingPeriods?.[i].length ?? 0,

        units,
      };

      buckets.push(bucket);
    }

    return {
      gradingPeriods: buckets ?? [],
    };
  }

  if (dataSource?.type === "UnitLayout") {
    const ul = dataSource.value;
    const gradingPeriods = ul.buckets?.map((bucket, index, allBuckets) => {
      const unitsInBucket = bucket.unitIds
        ?.map((unitRef) => {
          const courseUnit = courseUnits.find((u) => u.id === unitRef.id);

          if (!courseUnit?.unit) {
            return null;
          }

          return {
            unit: courseUnit.unit,
            daysInUnitInGradingPeriod:
              getUnitDurationFromCourseUnitTag(courseUnit),
            isContinued:
              index > 0 &&
              allBuckets[index - 1]?.unitIds?.find(
                (prevBucketUnitRef) => prevBucketUnitRef.id === unitRef.id,
              ) != null,
          };
        })
        .filter(exists);

      return {
        name: bucket.name ?? "Missing Bucket Name",
        semesterIndex:
          index < allBuckets.length / 2 ? (1 as const) : (2 as const),
        units: unitsInBucket ?? [],
      };
    });

    return {
      gradingPeriods: gradingPeriods ?? [],
    };
  }

  return {
    gradingPeriods: [],
  };
};
